import { useCallback, useEffect, useRef, useState } from 'react';
import { fetchAgentCallJourneyFilterDistinctValues } from '@/lib/axios/api/widgets.api';

export interface ColumnDistinctState {
  values: string[];
  hasMore: boolean;
  isLoading: boolean;
  /** True once this open column has received its first response. Stays true across re-queries. */
  loaded: boolean;
}

const EMPTY_STATE: ColumnDistinctState = { values: [], hasMore: false, isLoading: false, loaded: false };

function appliedFiltersFor(columnFilters: Partial<Record<string, string[] | null>>, column: string) {
  return Object.entries(columnFilters)
    .filter(([col]) => col !== column)
    .map(([col, vals]) => ({ column: col, values: vals ?? null }));
}

/**
 * Fetches distinct filter values per column independently — each open column owns its
 * own AbortController/page cursor, so opening column B never cancels column A's in-flight
 * request. Only a re-query of the SAME column (search/filter/date change) aborts its own
 * previous request.
 */
export function useAgentCallJourneyDistinctFilter(opts: {
  callIds: string[];
  openColumns: readonly string[];
  searchByColumn: Partial<Record<string, string>>;
  columnFilters: Partial<Record<string, string[] | null>>;
  startDate: string | undefined;
  endDate: string | undefined;
  enabled: boolean;
}) {
  const [state, setState] = useState<Record<string, ColumnDistinctState>>({});
  const abortRefs = useRef<Record<string, AbortController>>({});
  const pageRefs = useRef<Record<string, number>>({});
  const keyRefs = useRef<Record<string, string>>({});

  const fetchColumn = useCallback((column: string, pageIndex: number, append: boolean) => {
    if (!opts.startDate || !opts.endDate) return;
    abortRefs.current[column]?.abort();
    const controller = new AbortController();
    abortRefs.current[column] = controller;
    // Keep the previous values while re-querying (search/filter change): blanking them made the
    // list flash empty and made the column look "not loaded" to the popover, which then treated the
    // loading transition as an external reset and cleared the user's search text.
    setState((prev) => ({
      ...prev,
      [column]: { ...(prev[column] ?? EMPTY_STATE), isLoading: true },
    }));
    const appliedApiFilters = appliedFiltersFor(opts.columnFilters, column);
    fetchAgentCallJourneyFilterDistinctValues({
      callIds: opts.callIds,
      targetApiColumn: column,
      distinctSearch: opts.searchByColumn[column] ?? '',
      appliedApiFilters,
      startTime: opts.startDate,
      endTime: opts.endDate,
      pageIndex,
      signal: controller.signal,
    })
      .then((res) => {
        if (abortRefs.current[column] !== controller) return;
        pageRefs.current[column] = pageIndex;
        setState((prev) => {
          const cur = prev[column] ?? EMPTY_STATE;
          return { ...prev, [column]: { values: append ? [...cur.values, ...res.values] : res.values, hasMore: res.hasMore, isLoading: false, loaded: true } };
        });
      })
      .catch((err) => {
        if (err?.name === 'CanceledError' || err?.name === 'AbortError') return;
        if (abortRefs.current[column] === controller) {
          setState((prev) => ({ ...prev, [column]: { ...(prev[column] ?? EMPTY_STATE), isLoading: false } }));
        }
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opts.callIds, opts.columnFilters, opts.searchByColumn, opts.startDate, opts.endDate]);

  // Content-based signature, NOT the raw array/object references — opts.callIds/openColumns/etc
  // can be re-created (new reference, same content) on every render by the caller, and keying the
  // effect on those references directly would re-run it (and its setState calls) every render,
  // causing an infinite render loop. Stringifying collapses that to a stable primitive.
  const depsKey = JSON.stringify([
    opts.enabled,
    opts.callIds,
    opts.openColumns,
    opts.searchByColumn,
    opts.columnFilters,
    opts.startDate,
    opts.endDate,
  ]);

  useEffect(() => {
    const canQuery = opts.enabled && opts.callIds.length > 0 && !!opts.startDate && !!opts.endDate;
    if (!canQuery) {
      Object.values(abortRefs.current).forEach((c) => c?.abort());
      abortRefs.current = {};
      keyRefs.current = {};
      pageRefs.current = {};
      setState((prev) => (Object.keys(prev).length === 0 ? prev : {}));
      return;
    }

    const openSet = new Set(opts.openColumns);

    for (const column of opts.openColumns) {
      const key = JSON.stringify([
        opts.callIds,
        column,
        opts.searchByColumn[column] ?? '',
        appliedFiltersFor(opts.columnFilters, column),
        opts.startDate,
        opts.endDate,
      ]);
      if (keyRefs.current[column] === key) continue;
      keyRefs.current[column] = key;
      pageRefs.current[column] = 0;
      fetchColumn(column, 0, false);
    }

    for (const column of Object.keys(keyRefs.current)) {
      if (openSet.has(column)) continue;
      abortRefs.current[column]?.abort();
      delete abortRefs.current[column];
      delete keyRefs.current[column];
      delete pageRefs.current[column];
      setState((prev) => {
        if (!(column in prev)) return prev;
        const next = { ...prev };
        delete next[column];
        return next;
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [depsKey]);

  useEffect(() => () => {
    Object.values(abortRefs.current).forEach((c) => c?.abort());
  }, []);

  const getState = useCallback((column: string): ColumnDistinctState => state[column] ?? EMPTY_STATE, [state]);

  const loadMore = useCallback((column: string) => {
    const cur = state[column];
    if (!cur || cur.isLoading || !cur.hasMore) return;
    const nextPage = (pageRefs.current[column] ?? 0) + 1;
    fetchColumn(column, nextPage, true);
  }, [state, fetchColumn]);

  return { getState, loadMore };
}
